import React, { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import { useWorldStore } from '../state/worldStore';
import { useCityStore } from '../city/cityStore';
import { cityBridge } from '../render/cityBridge';
import { roadMapBridge } from '../render/roadMapBridge';
import { buildRoadMap } from '../render/roadMap';
import { listWorldObjects } from '../services/worldObjects';
import { CITY_NO_PARKING, KEEP_CLEAR } from '../city/cityZones';
import { pickBuildings, surveySites } from '../city/siteSurvey';
import { Outline, PlanLabel } from './VenueFootprints';

// SiteSurvey — the in-scene half of ⚙ → Sites (SitesPanel is the DOM half).
//
// RUNS the survey when the panel asks (city/siteSurvey has the rules): measures
// every node of the city model, keeps the buildings, reads the road map for
// their fronts, and hands the candidates to the store. In here rather than in
// the panel because the road map is a photograph of the city, and if the street
// lights have not taken one yet (the classic look never does) this needs the
// renderer to take its own — kept local, never published, so nothing else in
// the city changes because an admin pressed a button.
//
// DRAWS the answer while the tool is open, the measuring-tape way VenueFootprints
// does (depth off, above the kerbs, labels screen-sized):
//   amber      a candidate from this survey — unsaved until Save
//   blue dash  a site already saved (what Save will replace)
//   pink       a site a venue already stands on — taken
// Each with a tick from its door out to the street, so a wrong front is obvious.

const Y = 0.66;
const C_NEW = '#ffd27d';
const C_SAVED = '#7dd3ff';
const C_TAKEN = '#ff6bd6';

/** Every node of the model with its world box, parent first: [{ name, parent, minX..maxZ }]. */
function measureNodes(root) {
  root.updateMatrixWorld(true);
  const boxes = new Map();
  const tmp = new THREE.Box3();
  const boxOf = (o) => {
    const b = new THREE.Box3();
    if (o.isMesh && o.geometry && !o.isBatchedMesh) {
      if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
      if (o.geometry.boundingBox) b.copy(tmp.copy(o.geometry.boundingBox).applyMatrix4(o.matrixWorld));
    }
    for (const c of o.children) { if (!c.isBatchedMesh) b.union(boxOf(c)); }
    boxes.set(o, b);
    return b;
  };
  boxOf(root);
  const out = [];
  const index = new Map();
  root.traverse((o) => {
    const b = boxes.get(o);
    if (!b) return;   // inside a batch
    const parent = o.parent && index.has(o.parent) ? index.get(o.parent) : -1;
    index.set(o, out.length);
    out.push({ name: o.name || '', parent, minX: b.min.x, maxX: b.max.x, minY: b.min.y, maxY: b.max.y, minZ: b.min.z, maxZ: b.max.z });
  });
  return out;
}

function Tick({ from, rotY, color }) {
  const geo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const to = [from[0] + Math.sin(rotY) * 1.8, from[1] + Math.cos(rotY) * 1.8];
    g.setAttribute('position', new THREE.Float32BufferAttribute([from[0], Y, from[1], to[0], Y, to[1]], 3));
    return g;
  }, [from, rotY]);
  useEffect(() => () => geo.dispose(), [geo]);
  return (
    <lineSegments geometry={geo}>
      <lineBasicMaterial color={color} depthTest={false} depthWrite={false} fog={false} toneMapped={false} />
    </lineSegments>
  );
}

function Ghost({ x, z, rotY, w, d, door, color, dashed = false, label }) {
  return (
    <group>
      <group position={[x, 0, z]} rotation={[0, rotY || 0, 0]}>
        <Outline cx={0} cz={0} w={w} d={d} color={color} y={Y} dashed={dashed} />
      </group>
      {door && <Tick from={door} rotY={rotY || 0} color={color} />}
      {label && <PlanLabel pos={[x, Y, z]} color={color}>{label}</PlanLabel>}
    </group>
  );
}

export default function SiteSurvey() {
  const ON = useWorldStore((s) => s.adminTool) === 'sites';
  const ask = useWorldStore((s) => s.siteSurveyAsk);
  const survey = useWorldStore((s) => s.siteSurvey);
  const saved = useWorldStore((s) => s.savedSites);
  const registry = useWorldStore((s) => s.venueRegistry);
  const gl = useThree((s) => s.gl);

  useEffect(() => {
    if (!ON || !ask) return undefined;
    let dead = false;
    const done = (r) => { if (!dead) useWorldStore.getState().setSiteSurvey({ at: Date.now(), sites: [], rejected: null, note: '', ...r }); };
    (async () => {
      const model = cityBridge.model;
      if (!model) { done({ note: 'The city model is not loaded here — open the World with ?city=model.' }); return; }
      let map = roadMapBridge.map;
      if (!map?.cls && cityBridge.batches && cityBridge.bounds) {
        try { map = buildRoadMap(gl, model, cityBridge.batches, cityBridge.bounds); } catch (e) { console.warn('[sites] could not photograph the road map', e); }
      }
      if (!map?.cls) { done({ note: 'No road map yet — stand on the street for a few seconds and survey again.' }); return; }
      const t0 = performance.now();
      const buildings = pickBuildings(measureNodes(model));
      const ws = useWorldStore.getState();
      // Taken: every venue standing now (the built-ins, the registry's, any
      // moved by hand — venueAnchors holds them all).
      const anchors = Object.values(ws.venueAnchors || {}).map((a) => [a[0], a[2]]);
      // The open places (the plaza, the park, the court — any city place that is
      // not a venue front), the subway's mouths, and the arrival.
      const places = useCityStore.getState().snap?.places || [];
      const portals = await listWorldObjects(null, 'portal');
      if (dead) return;
      const points = [
        ...places.filter((p) => !p.anchor && Array.isArray(p.at)).map((p) => [p.at[0], p.at[1], 12]),
        ...portals.filter((o) => o.zone_id === 'street' && (o.data?.pit || (o.data?.target?.ref ?? o.ref) === 'subway')).map((o) => [o.pos[0], o.pos[2], 12]),
        ...KEEP_CLEAR.map(([x, z, r]) => [x, z, r + 4]),
      ];
      const { sites, rejected } = surveySites({ buildings, map, anchors, keepOut: { rects: CITY_NO_PARKING, points } });
      console.log(`[sites] surveyed ${buildings.length} buildings in ${Math.round(performance.now() - t0)} ms → ${sites.length} site(s); turned down: `
        + Object.entries(rejected).map(([k, n]) => `${k} ${n}`).join(', '));
      done({ sites, rejected, buildings: buildings.length });
    })();
    return () => { dead = true; };
  }, [ON, ask, gl]);

  if (!ON) return null;
  const sites = survey?.sites || [];
  return (
    <group renderOrder={998}>
      {saved.map((r) => (
        <Ghost key={`s${r.object_id}`} x={r.pos[0]} z={r.pos[2]} rotY={r.rotY} w={r.data?.w || 4} d={r.data?.d || 4}
          door={Array.isArray(r.data?.door) ? r.data.door : null} color={C_SAVED} dashed />
      ))}
      {Object.values(registry || {}).map((v) => (
        <Ghost key={`v${v.slug}`} x={v.site.x} z={v.site.z} rotY={v.site.rotY} w={v.site.w} d={v.site.d}
          door={v.site.door} color={C_TAKEN} label={v.name} />
      ))}
      {sites.map((s) => (
        <Ghost key={`n${s.ref}`} x={s.x} z={s.z} rotY={s.rotY} w={s.w} d={s.d} door={s.door} color={C_NEW}
          label={`${s.w.toFixed(0)}×${s.d.toFixed(0)}`} />
      ))}
    </group>
  );
}
